import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input, OnChanges, SimpleChanges, computed } from '@angular/core';
import { Params, Router } from "@angular/router";
import {StringUtils} from "../../utils/string-utils";
import {TaskResponseService} from "../../services/task-response-service";
import {IecTaskService} from "../../services/iec-task-service";
import {IecTask} from "../iec-task";
import {ShippingChargeTaskResponse} from "../../model/shipping-charge-task-response";
import {firstValueFrom} from "rxjs";
import {Entity, EntityKey} from "../../entity/entity";
import {EntityService} from "../../services/entity-service";
import {EntityUtils} from "../../utils/entity-utils";

import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {MatOption} from "@angular/material/autocomplete";
import {MatSelect} from "@angular/material/select";
import {MatButton} from "@angular/material/button";
import {FormsModule} from "@angular/forms";
import {SelectionService} from "../../services/selection-service";
import {SelectionItem} from "../../selections/selection-item";
import {MatInput} from "@angular/material/input";

@Component({
    selector: 'app-assign-shipping-charge-task',
    imports: [
    MatProgressSpinner,
    MatFormField,
    MatLabel,
    MatOption,
    MatSelect,
    MatButton,
    FormsModule,
    MatInput
],
    templateUrl: './assign-shipping-charge-task.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrl: './assign-shipping-charge-task.component.css'
})
export class AssignShippingChargeTaskComponent implements OnChanges, OnInit {
  private readonly router = inject(Router);
  private readonly responseService = inject(TaskResponseService);
  private readonly taskService = inject(IecTaskService);
  private readonly entityService = inject(EntityService);
  private readonly selectionService = inject(SelectionService);
  readonly taskIdParam = input.required<string>({ alias: 'taskId' });
  private readonly taskId = computed(() => +this.taskIdParam());
  entity: Entity | undefined;
  taskResponse: ShippingChargeTaskResponse | undefined;
  states: SelectionItem[] = [];
  isClosed: boolean = false;
  readonly isBusy = signal<boolean>(false);
  readonly errorMessage = signal<string | undefined>(undefined!);

  backToHome(): void {
    this.router.navigate(['/', 'main']).then(() => this.isBusy.set(false));
  }

  private handleError(error: any): void {
    this.errorMessage.set(StringUtils.getErrorMessage(error));
    this.isBusy.set(false);
  }

  ngOnInit(): void {
    this.initialize();
    this.isBusy.set(true);
    this.loadTaskData().then(() => this.isBusy.set(false), (error: any) => this.handleError(error));
  }

  private async loadTaskData(): Promise<void> {
    const task: IecTask = await firstValueFrom<IecTask>(this.taskService.getTask(this.taskId()));
    if (!task) {
      this.backToHome();
    }
    if (task.endTime && StringUtils.isNotBlank(task.responder)) {
      this.isClosed = true;
    } else {
      const entityKey: EntityKey = new EntityKey();
      entityKey.entityName = EntityUtils.determineEntityType(task.entityId);
      entityKey.entityId = task.entityId;
      entityKey.entityRevision = task.entityRevision;
      this.entity = await firstValueFrom<Entity>(this.entityService.getEntityByKey(entityKey));
      if (!this.entity) {
        this.backToHome();
      }
      this.states = await firstValueFrom<SelectionItem[]>(this.selectionService.getStateCodes());
      this.taskResponse = new ShippingChargeTaskResponse();
      this.taskResponse.taskId = task.id;
      this.taskResponse.stateCode = this.entity.overrideShipToStateCode;
    }
  }

  closeTask(): void {
    if (this.isValidResponse()) {
      this.responseService.completeShippingChargeTask(this.taskResponse!).subscribe({
        next: () => this.backToHome(),
        error: (error: any) => this.handleError(error)
      });
    }
  }

  isValidResponse(): boolean {
    return (this.taskResponse?.taskId || 0) > 0
      && StringUtils.isNotBlank(this.taskResponse?.stateCode)
      && !StringUtils.isEmptyNumericValue(this.taskResponse?.shippingCharge!);
  }


  private initialize(): void {
    if (StringUtils.isZeroOrEmpty(this.taskId())) {
      this.backToHome();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['taskIdParam'].firstChange) {
      this.initialize();
    }
  }
}
